// Bootstrap F2/F3: mapas reais com timer/stages/medalhas. Telas completas na F6.
import * as THREE from 'three';
import { formatTime, medalFor, MEDAL_ICON, MEDAL_LABEL, Medal } from '@surfrun/shared';
import { GameLoop } from './core/loop';
import { Input } from './core/input';
import { playerMove, makePlayerState, InputCmd } from './physics/move';
import { v3, copy, len, len2D } from './physics/vec';
import type { Brush } from './physics/trace';
import type { GameMap } from './map/types';
import { MAPS } from './map/maps/index';
import { GameScene } from './render/scene';
import { FPCamera } from './game/camera';
import { Run } from './game/run';
import { AudioEngine } from './audio/engine';
import { Settings, loadSettings, saveSettings } from './save/settings';
import { getPB, submitTime, allPBs, getPending, setPending, pushPending } from './save/local';
import { api, ApiError, Session, BoardRow } from './net/api';
import { PHYSICS_VERSION, GHOST_SAMPLE_DIV, TICK_RATE, GhostData, EYE_HEIGHT, UNIT_TO_METERS } from '@surfrun/shared';
import { GhostRecorder, GhostPlayer, GhostSlot, saveLocalGhost, loadLocalGhost, fetchGhost } from './game/ghost';

const $ = (id: string): HTMLElement => document.getElementById(id)!;
const app = $('app');
const overlay = $('menu');
const hudSpeed = $('speed');
const hudTimer = $('timer');
const hudStage = $('stage');
const hudPractice = $('practice');
const hudMapName = $('mapname');
const hudDebug = $('debug');
const finishPanel = $('finish');

const settings: Settings = loadSettings();

// --- renderer ---
const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
app.appendChild(renderer.domElement);

const gameScene = new GameScene();
const fpCam = new FPCamera(window.innerWidth / window.innerHeight);

window.addEventListener('resize', () => {
  renderer.setSize(window.innerWidth, window.innerHeight);
  fpCam.resize(window.innerWidth / window.innerHeight);
});

// --- estado de jogo ---
const player = makePlayerState();
const audio = new AudioEngine();
const input = new Input(renderer.domElement);
let currentMap: GameMap = MAPS[0]!.build();
let brushes: Brush[] = [];
let run: Run = null!;

// ============ ghosts ============
const recorder = new GhostRecorder();
let ghosts: GhostPlayer[] = [];
let showPbGhost = true;
let lastRunId = 0; // p/ link de duelo

function disposeGhosts(keepRivals = false): void {
  ghosts = ghosts.filter((g) => {
    if (keepRivals && g.slot !== 'pb') return true;
    g.dispose();
    return false;
  });
}

function addGhost(data: GhostData, username: string, slot: GhostSlot): void {
  const idx = ghosts.findIndex((g) => g.slot === slot);
  if (idx >= 0) { ghosts[idx]!.dispose(); ghosts.splice(idx, 1); }
  const g = new GhostPlayer(data, username, slot);
  gameScene.world.add(g.group);
  ghosts.push(g);
}

async function attachPbGhost(): Promise<void> {
  const existing = ghosts.find((g) => g.slot === 'pb');
  if (existing) { existing.dispose(); ghosts = ghosts.filter((g) => g !== existing); }
  if (!showPbGhost) return;
  const data = await loadLocalGhost(currentMap.slug);
  if (data) addGhost(data, 'seu PB', 'pb');
}

/** ghost de referência p/ deltas: rival1 > pb */
function mainGhost(): GhostPlayer | null {
  return ghosts.find((g) => g.slot === 'rival1') ?? ghosts.find((g) => g.slot === 'pb') ?? null;
}

let splitTimeout: ReturnType<typeof setTimeout> | null = null;
function showSplitDelta(stage: number): void {
  const g = mainGhost();
  if (!g) return;
  const ghostMs = g.splitAtStage(stage);
  if (ghostMs === null) return;
  const delta = run.timeMs - ghostMs;
  const el = $('split');
  el.textContent = `${delta >= 0 ? '+' : '−'}${formatTime(Math.abs(delta))}`;
  el.className = `mono show ${delta <= 0 ? 'better' : 'worse'}`;
  if (splitTimeout) clearTimeout(splitTimeout);
  splitTimeout = setTimeout(() => el.classList.remove('show'), 2000);
}

// ============ espectador ============
interface SpectateState {
  ghost: GhostPlayer;
  f: number;        // índice (fracionário) do sample
  speed: number;
  paused: boolean;
  pov: boolean;
}
let spectate: SpectateState | null = null;
const _specSample = [0, 0, 0, 0, 0];

function enterSpectate(data: GhostData, username: string): void {
  exitSpectate();
  const g = new GhostPlayer(data, username, 'rival1');
  gameScene.world.add(g.group);
  spectate = { ghost: g, f: 0, speed: 1, paused: false, pov: true };
  $('spec-who').textContent = `👁 assistindo ${username} · ${formatTime(data.timeMs)}`;
  $('spec-hud').classList.add('show');
  audio.init();
  input.requestLock();
}

function exitSpectate(): void {
  if (!spectate) return;
  spectate.ghost.dispose();
  spectate = null;
  $('spec-hud').classList.remove('show');
}

const setYaw = (yaw: number): void => { input.yaw = yaw; input.pitch = 0; };

function hideFinish(): void { finishPanel.classList.remove('show'); }

// ============ conta / sessão ============
let session: Session | null = null;

function refreshAccount(): void {
  const el = $('account');
  el.innerHTML = '';
  if (session) {
    const who = document.createElement('span');
    who.className = 'who';
    who.textContent = `👤 ${session.username} · ${session.points} pts`;
    const out = document.createElement('button');
    out.className = 'btn-sm';
    out.textContent = 'Sair';
    out.addEventListener('click', () => {
      void api.logout().catch(() => undefined);
      session = null;
      refreshAccount();
    });
    el.append(who, out);
  } else {
    const login = document.createElement('button');
    login.className = 'btn-sm';
    login.textContent = 'Entrar';
    login.addEventListener('click', () => openAuth('login'));
    const reg = document.createElement('button');
    reg.className = 'btn-sm';
    reg.textContent = 'Criar conta';
    reg.addEventListener('click', () => openAuth('register'));
    const note = document.createElement('span');
    note.style.cssText = 'font-size:11px;opacity:.55';
    note.textContent = 'tempos locais — entre p/ o ranking';
    el.append(login, reg, note);
  }
}

let authMode: 'login' | 'register' = 'login';
function openAuth(mode: 'login' | 'register'): void {
  authMode = mode;
  $('auth-title').textContent = mode === 'login' ? 'Entrar' : 'Criar conta';
  $('auth-submit').textContent = mode === 'login' ? 'Entrar' : 'Criar conta';
  $('auth-alt').textContent = mode === 'login' ? 'Não tem conta? Criar conta' : 'Já tem conta? Entrar';
  $('auth-email').style.display = mode === 'register' ? 'block' : 'none';
  $('auth-migrate-row').style.display = mode === 'register' && Object.keys(allPBs()).length ? 'flex' : 'none';
  ($('auth-pass') as HTMLInputElement).autocomplete = mode === 'login' ? 'current-password' : 'new-password';
  $('auth-msg').textContent = '';
  $('auth-modal').classList.add('show');
  ($('auth-user') as HTMLInputElement).focus();
}
$('auth-alt').addEventListener('click', () => openAuth(authMode === 'login' ? 'register' : 'login'));
$('auth-modal').addEventListener('click', (e) => {
  if (e.target === $('auth-modal')) $('auth-modal').classList.remove('show');
});
$('auth-submit').addEventListener('click', () => { void doAuth(); });
$('auth-pass').addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') void doAuth(); });

async function doAuth(): Promise<void> {
  const username = ($('auth-user') as HTMLInputElement).value.trim();
  const password = ($('auth-pass') as HTMLInputElement).value;
  const email = ($('auth-email') as HTMLInputElement).value.trim();
  const migrate = ($('auth-migrate') as HTMLInputElement).checked;
  $('auth-msg').textContent = '…';
  try {
    if (authMode === 'register') {
      await api.register(username, password, email || undefined, migrate ? allPBs() : undefined);
    } else {
      await api.login(username, password);
    }
    $('auth-modal').classList.remove('show');
    await initSession();
  } catch (e) {
    $('auth-msg').textContent = e instanceof ApiError ? e.message : 'erro inesperado';
  }
}

async function initSession(): Promise<void> {
  try {
    const me = await api.me();
    session = me.user;
    // merge dos PBs do servidor no store local (UI unificada)
    if (me.pbs) for (const [slug, t] of Object.entries(me.pbs)) submitTime(slug, t);
  } catch {
    session = null; // offline — segue local
  }
  refreshAccount();
  refreshMapList();
  void flushPending();
  void loadBoard();
}

async function flushPending(): Promise<void> {
  if (!session) return;
  let pending = getPending();
  while (pending.length) {
    const run = pending[0]!;
    try {
      await api.submitRun(run);
    } catch (e) {
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        // inválida/rejeitada — descarta
      } else {
        break; // offline/erro de servidor — tenta depois
      }
    }
    pending = pending.slice(1);
    setPending(pending);
  }
}

// ============ rivais e replays a partir do board ============
let rivalRunId = 0;

async function toggleRival(row: BoardRow): Promise<void> {
  if (rivalRunId === row.runId) {
    // desliga
    const g = ghosts.find((x) => x.slot === 'rival1');
    if (g) { g.dispose(); ghosts = ghosts.filter((x) => x !== g); }
    rivalRunId = 0;
    void loadBoard();
    return;
  }
  const data = await fetchGhost(row.runId);
  if (!data) { $('board-note').textContent = 'não consegui baixar esse ghost'; return; }
  addGhost(data, row.username, 'rival1');
  rivalRunId = row.runId;
  void loadBoard();
  input.requestLock(); // volta pro jogo já com o rival na pista
}

async function watchReplay(row: BoardRow): Promise<void> {
  const data = await fetchGhost(row.runId);
  if (!data) { $('board-note').textContent = 'não consegui baixar esse ghost'; return; }
  enterSpectate(data, row.username);
}

// ============ leaderboard do mapa atual ============
async function loadBoard(): Promise<void> {
  $('board-map').textContent = currentMap.name;
  const rows = $('board-rows');
  const note = $('board-note');
  const meEl = $('board-me');
  try {
    const b = await api.leaderboard(currentMap.slug);
    rows.innerHTML = '';
    for (const row of b.rows.slice(0, 10)) {
      const li = document.createElement('li');
      if (session && row.username === session.username) li.className = 'me';
      li.innerHTML = `<span class="rk mono">#${row.rank}</span><span>${row.username}</span><span class="mono">${formatTime(row.timeMs)}</span>`;
      const acts = document.createElement('span');
      acts.className = 'acts';
      if (row.hasGhost) {
        const race = document.createElement('button');
        race.className = 'gbtn' + (rivalRunId === row.runId ? ' on' : '');
        race.title = 'correr contra este ghost';
        race.textContent = '👻';
        race.addEventListener('click', () => { void toggleRival(row); });
        const watch = document.createElement('button');
        watch.className = 'gbtn';
        watch.title = 'assistir replay';
        watch.textContent = '▶';
        watch.addEventListener('click', () => { void watchReplay(row); });
        acts.append(race, watch);
      }
      const duel = document.createElement('button');
      duel.className = 'gbtn';
      duel.title = 'copiar link de duelo';
      duel.textContent = '🔗';
      duel.addEventListener('click', () => {
        void navigator.clipboard.writeText(`${location.origin}/#duel=${row.runId}`);
        duel.textContent = '✓';
        setTimeout(() => { duel.textContent = '🔗'; }, 1200);
      });
      acts.append(duel);
      li.appendChild(acts);
      rows.appendChild(li);
    }
    note.textContent = b.rows.length ? '' : 'Seja o primeiro a registrar um tempo!';
    meEl.textContent = b.myRank !== null && b.myTime !== null ? `você: #${b.myRank} · ${formatTime(b.myTime)}` : '';
  } catch (e) {
    rows.innerHTML = '';
    meEl.textContent = '';
    note.textContent = e instanceof ApiError && e.code === 'NO_DB'
      ? 'ranking indisponível no momento'
      : 'offline — ranking indisponível';
  }
}

async function handleFinish(timeMs: number, valid: boolean): Promise<void> {
  const ghostB64 = valid
    ? await recorder.finish(currentMap.slug, timeMs, run.splits.slice(0, currentMap.stages - 1))
    : null;
  const medal: Medal = valid ? medalFor(timeMs, currentMap.medals) : null;
  $('f-time').textContent = formatTime(timeMs);
  $('f-medal').textContent = valid && medal ? `${MEDAL_ICON[medal]} ${MEDAL_LABEL[medal]}` : '';
  $('f-practice').textContent = valid ? '' : 'PRÁTICA — tempo não conta';
  const deltaEl = $('f-delta');
  deltaEl.className = 'fdelta mono';
  if (valid) {
    const pb = getPB(currentMap.slug);
    const improved = submitTime(currentMap.slug, timeMs);
    if (pb === null) deltaEl.textContent = 'primeiro tempo!';
    else if (improved) { deltaEl.textContent = `−${formatTime(pb - timeMs)} novo PB!`; deltaEl.classList.add('better'); }
    else { deltaEl.textContent = `+${formatTime(timeMs - pb)} (PB ${formatTime(pb)})`; deltaEl.classList.add('worse'); }
    if (improved && ghostB64) {
      saveLocalGhost(currentMap.slug, ghostB64);
      void attachPbGhost();
    }
    refreshMapList();
  } else {
    deltaEl.textContent = '';
  }
  $('f-rank').textContent = '';
  $('f-duel').style.display = 'none';
  if (valid) void submitOnline(timeMs, ghostB64);
  finishPanel.classList.add('show');
  audio.playFinish(valid ? medal : null);
}

async function submitOnline(timeMs: number, ghostB64: string | null): Promise<void> {
  const payload = {
    mapSlug: currentMap.slug,
    timeMs,
    stageTimesMs: [...run.splits, timeMs],
    maxSpeed: run.maxSpeed,
    physicsVersion: PHYSICS_VERSION,
    ghost: ghostB64 ?? undefined,
  };
  if (!session) {
    $('f-rank').textContent = 'entre na sua conta para o ranking global';
    return;
  }
  try {
    const r = await api.submitRun(payload);
    if (r.flagged) $('f-rank').textContent = 'tempo em análise';
    else $('f-rank').textContent = `#${r.rank} mundial${r.improved ? '' : ' (PB online mantido)'}`;
    if (r.runId > 0) {
      lastRunId = r.runId;
      $('f-duel').style.display = 'inline-block';
    }
    void loadBoard();
  } catch (e) {
    if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
      $('f-rank').textContent = e.message;
    } else {
      pushPending(payload);
      $('f-rank').textContent = 'offline — tempo na fila de envio';
    }
  }
}

$('f-duel').addEventListener('click', () => {
  if (!lastRunId) return;
  void navigator.clipboard.writeText(`${location.origin}/#duel=${lastRunId}`);
  $('f-duel').textContent = '✓ link copiado!';
  setTimeout(() => { $('f-duel').textContent = '🔗 Copiar link de duelo'; }, 1500);
});

function loadMap(slug: string): void {
  exitSpectate();
  disposeGhosts();
  const entry = MAPS.find((m) => m.slug === slug) ?? MAPS[0]!;
  currentMap = entry.build();
  gameScene.clearWorld();
  gameScene.addBrushes(currentMap.built);
  gameScene.addZoneMarkers(currentMap.zones.map((z) => ({ kind: z.kind, min: z.min, max: z.max })));
  gameScene.setEnv(currentMap.env, currentMap.hazard, currentMap.killY);
  brushes = currentMap.built.map((b) => b.brush);
  run = new Run(currentMap, {
    onStart: () => { audio.playRunStart(); recorder.reset(); },
    onStageEnter: (stage) => { audio.playStage(); showSplitDelta(stage); },
    onFinish: (timeMs, valid) => { void handleFinish(timeMs, valid); },
    onRespawn: () => { audio.playTeleport(); hideFinish(); },
  });
  run.resetRun(player, setYaw);
  copy(prevPos, player.pos);
  hudMapName.textContent = `${currentMap.name} · T${currentMap.tier}`;
  hideFinish();
  lastRunId = 0;
  void attachPbGhost();
  refreshMapList();
  void loadBoard();
}

// --- lista de mapas no menu ---
function refreshMapList(): void {
  const list = $('map-list');
  list.innerHTML = '';
  for (const m of MAPS) {
    const pb = getPB(m.slug);
    const def = m.build(); // leve (geometria só é usada no load)
    const medal = pb !== null ? medalFor(pb, def.medals) : null;
    const btn = document.createElement('button');
    btn.className = 'map-btn' + (m.slug === currentMap.slug ? ' active' : '');
    btn.innerHTML =
      `<span class="tier t${m.tier}">T${m.tier}</span>` +
      `<span>${m.name}</span>` +
      `<span class="pb mono">${pb !== null ? formatTime(pb) : '—'}</span>` +
      `<span class="medal">${medal ? MEDAL_ICON[medal] : ''}</span>`;
    btn.addEventListener('click', () => {
      audio.init();
      loadMap(m.slug);
      input.requestLock();
    });
    list.appendChild(btn);
  }
}

// --- input / menu ---
$('play-btn').addEventListener('click', () => {
  audio.init();
  input.requestLock();
});
input.onLockChange = (locked) => {
  overlay.classList.toggle('hidden', locked);
  if (!locked) {
    void loadBoard();
    void flushPending();
  }
};
let debugOn = false;
input.onKeyDown = (code) => {
  if (!input.locked) return;
  if (spectate) {
    const s = spectate;
    if (code === 'Space') s.paused = !s.paused;
    if (code === 'ArrowLeft') s.f = Math.max(0, s.f - (5 * TICK_RATE) / GHOST_SAMPLE_DIV);
    if (code === 'ArrowRight') s.f = Math.min(s.ghost.data.sampleCount - 1, s.f + (5 * TICK_RATE) / GHOST_SAMPLE_DIV);
    if (code === 'Digit1') s.speed = 0.5;
    if (code === 'Digit2') s.speed = 1;
    if (code === 'Digit3') s.speed = 2;
    if (code === 'KeyC') s.pov = !s.pov;
    return;
  }
  if (code === 'KeyR') run.resetRun(player, setYaw);
  if (code === 'KeyE') run.resetStage(player, setYaw);
  if (code === 'KeyG') {
    showPbGhost = !showPbGhost;
    void attachPbGhost();
  }
  if (code === 'F3') {
    debugOn = !debugOn;
    hudDebug.style.display = debugOn ? 'block' : 'none';
  }
};

// --- settings ---
function applySettings(): void {
  input.sensitivity = settings.sens;
  fpCam.setFov(settings.fov);
  fpCam.viewRollEnabled = settings.viewRoll;
  fpCam.landBobEnabled = settings.landBob;
  audio.setVolumes(settings.volMaster, settings.volWind, settings.volSfx, settings.volMusic);
}
function bindRange(id: string, outId: string, get: () => number, set: (v: number) => void, fmt: (v: number) => string): void {
  const el = $(id) as HTMLInputElement;
  const out = $(outId);
  el.value = String(get());
  out.textContent = fmt(get());
  el.addEventListener('input', () => {
    set(Number(el.value));
    out.textContent = fmt(Number(el.value));
    applySettings();
    saveSettings(settings);
  });
}
function bindCheck(id: string, get: () => boolean, set: (v: boolean) => void): void {
  const el = $(id) as HTMLInputElement;
  el.checked = get();
  el.addEventListener('change', () => {
    set(el.checked);
    applySettings();
    saveSettings(settings);
  });
}
bindRange('s-sens', 'o-sens', () => settings.sens, (v) => { settings.sens = v; }, (v) => v.toFixed(2));
bindRange('s-fov', 'o-fov', () => settings.fov, (v) => { settings.fov = v; }, (v) => String(v));
bindRange('s-master', 'o-master', () => settings.volMaster, (v) => { settings.volMaster = v; }, (v) => String(v));
bindRange('s-wind', 'o-wind', () => settings.volWind, (v) => { settings.volWind = v; }, (v) => String(v));
bindRange('s-sfx', 'o-sfx', () => settings.volSfx, (v) => { settings.volSfx = v; }, (v) => String(v));
bindRange('s-music', 'o-music', () => settings.volMusic, (v) => { settings.volMusic = v; }, (v) => String(v));
bindCheck('s-roll', () => settings.viewRoll, (v) => { settings.viewRoll = v; });
bindCheck('s-bob', () => settings.landBob, (v) => { settings.landBob = v; });
applySettings();

// --- loop ---
const prevPos = v3();
const interpPos = v3();
const cmd: InputCmd = { fwd: 0, side: 0, jump: false, duck: false, yaw: 0, pitch: 0 };
let fps = 0, fpsAcc = 0, fpsCount = 0;

const DEG = Math.PI / 180;
const loop = new GameLoop({
  tick: () => {
    if (spectate) {
      if (!spectate.paused) {
        spectate.f += spectate.speed / GHOST_SAMPLE_DIV;
        const max = spectate.ghost.data.sampleCount - 1;
        if (spectate.f >= max) { spectate.f = max; spectate.paused = true; }
      }
      return;
    }
    copy(prevPos, player.pos);
    if (input.locked) {
      input.sample(cmd);
    } else {
      cmd.fwd = 0; cmd.side = 0; cmd.jump = false; cmd.duck = false;
    }
    playerMove(player, cmd, brushes);
    run.update(player, setYaw);
    if (run.state === 'running') recorder.record(player, cmd.yaw, cmd.pitch);

    if (player.justJumped) audio.playJump();
    if (player.justLanded && player.landSpeed > 200) {
      audio.playLand(player.landSpeed);
      fpCam.onLand(player.landSpeed);
    }
  },
  render: (alpha, dtReal) => {
    gameScene.update(dtReal);

    if (spectate) {
      const s = spectate;
      s.ghost.update(s.f * GHOST_SAMPLE_DIV);
      s.ghost.group.visible = !s.pov;
      s.ghost.sampleAt(s.f, _specSample);
      const [gx, gy, gz, gyaw, gpitch] = _specSample as [number, number, number, number, number];
      if (s.pov) {
        fpCam.camera.position.set(gx * UNIT_TO_METERS, (gy + EYE_HEIGHT) * UNIT_TO_METERS, gz * UNIT_TO_METERS);
        fpCam.camera.rotation.set(gpitch * DEG, gyaw * DEG, 0);
      } else {
        const back = 260, up = 140;
        fpCam.camera.position.set(
          (gx + Math.sin(gyaw * DEG) * back) * UNIT_TO_METERS,
          (gy + up) * UNIT_TO_METERS,
          (gz + Math.cos(gyaw * DEG) * back) * UNIT_TO_METERS,
        );
        fpCam.camera.lookAt(gx * UNIT_TO_METERS, (gy + 48) * UNIT_TO_METERS, gz * UNIT_TO_METERS);
      }
      const specMs = (s.f * GHOST_SAMPLE_DIV * 1000) / TICK_RATE;
      hudTimer.textContent = formatTime(specMs);
      hudTimer.className = 'mono';
      hudSpeed.innerHTML = '';
      hudStage.textContent = '';
      audio.wind.update(0);
      renderer.render(gameScene.scene, fpCam.camera);
      return;
    }

    interpPos.x = prevPos.x + (player.pos.x - prevPos.x) * alpha;
    interpPos.y = prevPos.y + (player.pos.y - prevPos.y) * alpha;
    interpPos.z = prevPos.z + (player.pos.z - prevPos.z) * alpha;
    fpCam.update(interpPos, player.ducked, input.yaw, input.pitch, cmd.side, dtReal);

    // ghosts correm sincronizados com o tick da run local
    const raceTicks = run.state === 'running' ? run.ticks + alpha : 0;
    for (const g of ghosts) g.update(raceTicks);

    audio.wind.update(len(player.vel));
    hudSpeed.innerHTML = `${Math.round(len2D(player.vel))}<span class="u"> u/s</span>`;
    hudTimer.textContent = formatTime(run.timeMs);
    hudTimer.className = `mono ${run.state === 'idle' ? 'idle' : run.state === 'finished' ? 'finished' : ''}`;
    hudStage.textContent = currentMap.stages > 1 ? `S${run.currentStage}/${currentMap.stages}` : '';
    hudPractice.style.display = run.valid ? 'none' : 'block';

    fpsAcc += dtReal; fpsCount++;
    if (fpsAcc >= 0.5) { fps = Math.round(fpsCount / fpsAcc); fpsAcc = 0; fpsCount = 0; }
    if (debugOn) {
      hudDebug.textContent =
        `fps ${fps}\n` +
        `pos ${player.pos.x.toFixed(1)} ${player.pos.y.toFixed(1)} ${player.pos.z.toFixed(1)}\n` +
        `vel ${player.vel.x.toFixed(0)} ${player.vel.y.toFixed(0)} ${player.vel.z.toFixed(0)} (|${len(player.vel).toFixed(0)}|)\n` +
        `grounded ${player.grounded} stage ${run.currentStage} estado ${run.state}\n` +
        `yaw ${input.yaw.toFixed(1)} pitch ${input.pitch.toFixed(1)}`;
    }

    renderer.render(gameScene.scene, fpCam.camera);
  },
});

// ============ duelo por link (#duel=<runId>) ============
async function checkDuelLink(): Promise<void> {
  const m = /#duel=(\d+)/.exec(location.hash);
  if (!m) return;
  const runId = Number(m[1]);
  try {
    const meta = await api.runMeta(runId);
    loadMap(meta.mapSlug);
    const data = await fetchGhost(runId);
    if (data) {
      addGhost(data, meta.username, 'rival1');
      rivalRunId = runId;
      $('board-note').textContent = `⚔️ duelo contra ${meta.username} (${formatTime(meta.timeMs)}) — clique em JOGAR`;
    }
  } catch {
    $('board-note').textContent = 'link de duelo inválido ou expirado';
  }
}

refreshAccount();
loadMap(MAPS[0]!.slug);
void initSession();
void checkDuelLink();
loop.start();
